import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Lock, CheckCircle } from 'lucide-react';
import axios from 'axios';
import PasswordStrength, { isPasswordStrong } from '@/components/auth/PasswordStrength';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [strong, setStrong] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (!isPasswordStrong(password)) {
      toast.error('Password does not meet the policy');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="p-8 max-w-md w-full rounded-3xl text-center">
          <p className="text-red-600 font-semibold mb-4">Invalid reset link</p>
          <Link to="/login" className="text-teal-600 hover:underline">Go to Sign In</Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50 to-slate-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-slate-900">Travcations</h1>
        </div>
        <Card className="p-8 bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-slate-100" data-testid="reset-password-card">
          {done ? (
            <div className="text-center py-4" data-testid="reset-success-msg">
              <CheckCircle className="h-12 w-12 text-teal-600 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Password Reset!</h2>
              <p className="text-slate-500 text-sm mb-6">You can now sign in with your new password.</p>
              <Button onClick={() => navigate('/login')} className="bg-teal-600 text-white rounded-xl" data-testid="go-to-login-btn">
                Go to Sign In
              </Button>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-slate-900 mb-6 text-center">Set New Password</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 h-12 rounded-xl" placeholder="Set a strong password" required data-testid="input-new-password" />
                  </div>
                  <PasswordStrength password={password} onValidityChange={setStrong} />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="pl-10 h-12 rounded-xl" placeholder="Re-enter password" required data-testid="input-confirm-password" />
                  </div>
                </div>
                <Button type="submit" disabled={loading || !strong || password !== confirm} className="w-full h-12 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl font-semibold" data-testid="reset-password-btn">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
